import { useEffect, useMemo } from 'react'
import { filter, find, uniq } from 'lodash-es'
import { useModelStore, useTransmissionStore } from '@editorStores'

/** Selected element + vertical-transmission targets (same wiring as Vertikale Verbindungen “fokussieren”). */
export const collectFokusGuidsForElement = (
  graph: VerticalTransmissionGraph | null | undefined,
  elementGuid: string,
  beamGuidToSlabGuid?: ReadonlyMap<string, string>,
): string[] => {
  if (!graph) return [elementGuid]
  // A slab/roof-slab beam is selected by its beam guid, but the transmission graph keys the
  // slab's supports by the surface guid. Resolve to the surface guid so the slab's connected
  // (supporting) elements are collected; the returned focus set keeps the beam guid itself.
  const supportLookupGuid = beamGuidToSlabGuid?.get(elementGuid) ?? elementGuid
  const supports = filter(graph.element_supports ?? [], ['element_guid', supportLookupGuid])
  const targetElementGuids = supports.flatMap(support => {
    const links = filter(graph.support_targets ?? [], ['support_guid', support.guid])
    return links
      .map(({ target_guid }) => find(graph.element_targets ?? [], ['guid', target_guid]))
      .filter((t): t is ElementSupportItem => Boolean(t))
      .map(t => t.element_guid)
  })
  return uniq([elementGuid, ...targetElementGuids])
}

/**
 * When `fokusTransparency` is on: isolate selection + vertical transmission targets in the 3D transparency model.
 * When off or unmounted: restore transparency / translucent state.
 */
export function useResults2FokusEffect(
  fokusTransparency: boolean,
  selectedId: string | undefined,
  selectedIds: string[] | undefined,
): void {
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const focusElements = useModelStore(state => state.focusElements)
  const setTransparencyForGuids = useModelStore(state => state.setTransparencyForGuids)
  const clearTransparencyVisibilityModel = useModelStore(
    state => state.clearTransparencyVisibilityModel,
  )
  const setTranslucent = useModelStore(state => state.setTranslucent)
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)

  const beamGuidToSlabGuid = useMemo(() => {
    const m = new Map<string, string>()
    for (const slab of verticalSlabs) {
      if (slab.beam?.guid) m.set(slab.beam.guid, slab.guid)
    }
    for (const slab of verticalRoofSlabs) {
      if (slab.beam?.guid) m.set(slab.beam.guid, slab.guid)
    }
    return m
  }, [verticalRoofSlabs, verticalSlabs])

  const fokusTargetGuids = useMemo(() => {
    const primary = selectedIds?.length ? selectedIds : selectedId ? [selectedId] : []
    if (primary.length === 0) return []
    const merged = new Set<string>()
    for (const id of primary) {
      collectFokusGuidsForElement(verticalTransmissionGraph, id, beamGuidToSlabGuid).forEach(g =>
        merged.add(g),
      )
    }
    return [...merged]
  }, [verticalTransmissionGraph, selectedId, selectedIds, beamGuidToSlabGuid])

  const selectedBeamGuids = useMemo(() => {
    const primary = selectedIds?.length ? selectedIds : selectedId ? [selectedId] : []
    return primary.filter(id => beamGuidToSlabGuid.has(id))
  }, [beamGuidToSlabGuid, selectedId, selectedIds])

  const slabsToForceTransparent = useMemo(() => {
    const out = new Set<string>()
    for (const beamGuid of selectedBeamGuids) {
      const slabGuid = beamGuidToSlabGuid.get(beamGuid)
      if (slabGuid) out.add(slabGuid)
    }
    return [...out]
  }, [beamGuidToSlabGuid, selectedBeamGuids])

  useEffect(() => {
    if (!fokusTransparency) {
      clearTransparencyVisibilityModel()
      setTranslucent(false)
      return
    }
    if (fokusTargetGuids.length === 0) {
      clearTransparencyVisibilityModel()
      setTranslucent(false)
      return
    }
    focusElements(fokusTargetGuids)
    // Special case: slab-beams / roof-slab-beams selection should keep the slab itself transparent
    // so the beam stays visible/selectable even when the transmission graph includes the slab.
    if (slabsToForceTransparent.length > 0) {
      setTransparencyForGuids(slabsToForceTransparent, true)
    }
    return () => {
      clearTransparencyVisibilityModel()
      setTranslucent(false)
    }
  }, [
    clearTransparencyVisibilityModel,
    focusElements,
    fokusTransparency,
    fokusTargetGuids,
    setTransparencyForGuids,
    setTranslucent,
    slabsToForceTransparent,
  ])
}
